// Sending-domain shapes are generated from OrbitalX.Dto — see api-generated.d.ts.
//
// Status values are the C# enum NAMES, not descriptions: SendingDomainMapper uses .ToString().
// On the view DTO, `IsDefault` marks the company default (auto-selected for activations and test
// sends), and `TrackingSubdomain` is a custom label like "links" — null while on Resend's shared
// tracking domain.

export type {
  SendingDomainCreateDto,
  SendingDomainDnsRecordDto as DnsRecordDto,
  SendingDomainViewDto,
  ConfigureTrackingDto,
  SendingDomainDeveloperInstructionsDto,
  SendingDomainWarmupViewDto,
  SendingDomainWarmupTodayDto,
  SendingDomainRateWindowDto,
  SendingDomainRateLimitsDto,
  SendingDomainWarmupStepDto,
  SendingDomainStoppedDto,
  SendingDomainHeldBroadcastDto,
  SendingDomainWarmupSummaryDto,
  SendingDomainWarmupSummaryItemDto,
  SendingDomainForecastRequestDto,
  SendingDomainForecastViewDto,
  SendingDomainForecastDomainDto,
  SendingDomainForecastPlanDto,
  SendingDomainForecastDayDto,
  SendingDomainForecastLimitsDto,
} from './api-generated'

// Warm-up (ORB-953). Written by hand because the C# fields are strings: State, WebsiteCheckStatus and
// SendClass carry enum NAMES (SendingDomainMapper uses .ToString()), like Status.
export type SendingDomainState = 'SettingUp' | 'Stopped' | 'Warming' | 'Ready'
export type WebsiteCheckStatus = 'NotChecked' | 'Passed' | 'Failed'
export type WarmupSendClass = 'Registrant' | 'EventTimed' | 'Flexible'
export type SendingDomainRefusalCause = 'NotVerified' | 'WebsiteCheckNotPassed' | 'Stopped' | 'OverTodaysLimit'

/** What a domain's state pill shows: Warming with a hold reason reads Holding. */
export type SendingDomainPill = SendingDomainState | 'Holding'

export const SENDING_DOMAIN_PILL_LABELS: Record<SendingDomainPill, string> = {
  SettingUp: 'Setting up',
  Stopped: 'Stopped',
  Warming: 'Warming',
  Holding: 'Holding',
  Ready: 'Ready',
}

const SENDING_DOMAIN_STATES: readonly string[] = ['SettingUp', 'Stopped', 'Warming', 'Ready']

/**
 * The pill for a domain, or null (logged) for a State this build does not know, so a screen renders no
 * pill rather than a raw enum name.
 */
export function sendingDomainPill(domain: { State: string; WarmupHoldReason?: string | null }): SendingDomainPill | null {
  if (!SENDING_DOMAIN_STATES.includes(domain.State)) {
    console.error('Unknown sending domain state', domain.State)
    return null
  }
  const state = domain.State as SendingDomainState
  return state === 'Warming' && domain.WarmupHoldReason ? 'Holding' : state
}

// Resend delivery regions (Amazon SES) for data residency — EU first (the default).
export const SENDING_DOMAIN_REGIONS: { value: string; label: string }[] = [
  { value: 'eu-west-1', label: '🇮🇪 EU — Ireland (eu-west-1)' },
  { value: 'us-east-1', label: '🇺🇸 US East — N. Virginia (us-east-1)' },
  { value: 'sa-east-1', label: '🇧🇷 South America — São Paulo (sa-east-1)' },
  { value: 'ap-northeast-1', label: '🇯🇵 Asia Pacific — Tokyo (ap-northeast-1)' },
]

export const DEFAULT_SENDING_DOMAIN_REGION = 'eu-west-1'

// Overall verification is "done" at these states — polling stops here.
export const TERMINAL_DOMAIN_STATUSES = ['Verified', 'Failed', 'PartiallyFailed']

// Shared by both overall and per-record status pills (do NOT reuse INTEGRATION_STATUS_COLORS,
// which is keyed for integration health, not DNS verification).
export const DOMAIN_STATUS_COLORS: Record<string, string> = {
  NotStarted: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  Pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  Verified: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  TemporaryFailure: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  PartiallyVerified: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  PartiallyFailed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

export const DOMAIN_STATUS_LABELS: Record<string, string> = {
  NotStarted: 'Not started',
  Pending: 'Pending',
  Verified: 'Verified',
  Failed: 'Failed',
  TemporaryFailure: 'Temporary failure',
  PartiallyVerified: 'Partially verified',
  PartiallyFailed: 'Partially failed',
}
